import { PlusOutlined } from '@ant-design/icons';
import {
  AdvancedSearchSelectInput,
  useFormAugmentedContext,
} from '@framework/form';
import { useTranslation } from '@framework/i18n';
import { Flex, Space } from 'antd';
import { groupBy } from 'lodash';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { down } from 'styled-breakpoints';
import styled from 'styled-components';
import { v4 } from 'uuid';
import { InputLikeText } from '../../input/InputLikeText';
import {
  SearchableField,
  SearchableFieldGroup,
  SearchableFieldRow,
} from '../ISearchableField';
import { getSearchableFieldNames } from '../getSearchableFieldNames';
import { isSearchableFieldRowActive } from '../isSearchableFieldRowActive';
import { AdvancedSearchField } from './AdvancedSearchField';
import { AdvancedSearchFormItem } from './AdvancedSearchFormItem';
import { SearchActions } from './SearchActions';
import { SearchRow } from './SearchRow';

const AdvancedSearchContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const HideOnMobileSpan = styled.span`
  ${down('sm')} {
    display: none;
  }
`;

export interface IAdvancedSearchFormSectionProps {
  id: string;
  fields: SearchableField[];
  query: Record<string, any>;
  defaultFieldName?: string;
  /**
   * Pulsanti di azione supplementari
   */
  extraActions?: React.ReactNode;
}

function getRows(
  mergedActiveRows: SearchableFieldRow[],
  defaultFieldName: string | undefined,
): SearchableFieldRow[] {
  if (mergedActiveRows?.length > 0) {
    return mergedActiveRows;
  }

  if (defaultFieldName) {
    return [{ id: v4(), name: defaultFieldName }];
  }

  return [];
}

/**
 * All'interno del FormHandler della ricerca avanzata, gestisce i campi
 * dei singoli filtri.
 * Coordina attraverso `localStorage` la lista dei filtri disponibili e
 * visualizzati.
 */
export function AdvancedSearchFormSection(
  props: IAdvancedSearchFormSectionProps,
) {
  const { fields, query, defaultFieldName, id, extraActions } = props;
  const methods = useFormContext();
  const augmented = useFormAugmentedContext();
  const { t } = useTranslation();

  const mergedActiveRows = useMemo(() => {
    const activeRows = fields
      .filter(f => isSearchableFieldRowActive(query ?? {}, f) && !f.hidden)
      .map(f => ({ id: v4(), name: f.name }));

    const previousRows = (
      JSON.parse(
        localStorage.getItem(`advsrc-${id}`) ?? '[]',
      ) as SearchableFieldRow[]
    )
      // Ignoro i campi nascosti
      .filter(row => !fields.find(f => f.name === row.name)?.hidden);

    return [
      ...previousRows.filter(row => fields.some(f => f.name === row.name)),
      ...activeRows.filter(row => !previousRows.some(r => r.name === row.name)),
    ];
  }, [fields, id, query]);

  const [rows, setRows] = useState<SearchableFieldRow[]>(
    getRows(mergedActiveRows, defaultFieldName),
  );

  useEffect(() => {
    setRows(getRows(mergedActiveRows, defaultFieldName));
  }, [mergedActiveRows, defaultFieldName]);

  useEffect(() => {
    localStorage.setItem(`advsrc-${id}`, JSON.stringify(rows));
  }, [rows, id]);

  const filterList = useMemo(() => {
    const visibles = fields.filter(
      field => !rows.some(i => i.name === field.name) && !field.hidden,
    );

    // Raggruppo i filtri disponibili
    const grouped = groupBy(visibles, 'group');

    // Se sono presenti tag semantici, creo il gruppo dedicato
    const semanticTagFileters = grouped[SearchableFieldGroup.SemanticTags]
      ? [
          {
            label: t(
              'advanced-search.tag-semantici',
              'Altri campi specializzati',
            ),
            title: 'tag-semantici',
            key: SearchableFieldGroup.SemanticTags,
            options: grouped[SearchableFieldGroup.SemanticTags]
              ?.map(field => ({
                label: field.label,
                value: field.name,
                disabled: rows.some(i => i.name === field.name),
              }))
              .sort((a, b) => a.label.localeCompare(b.label)),
          },
        ]
      : [];

    // Genero le liste di filtri disponibili divisi per gruppo
    const result = [
      // Filtri
      {
        label: t('advanced-search.filtri', 'Filtri'),
        title: 'filtri',
        key: SearchableFieldGroup.Filters,
        options: grouped[SearchableFieldGroup.Filters]
          ?.map(field => ({
            label: field.label,
            value: field.name,
            disabled: rows.some(i => i.name === field.name),
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      },
      // Tag Semantici
      ...semanticTagFileters,
    ];
    return result;
  }, [fields, rows, t]);

  return (
    <AdvancedSearchContainer>
      <Flex gap={8} vertical>
        {rows.map((fieldRow, index) => {
          const field = fields.find(f => f.name === fieldRow.name);
          return (
            <SearchRow
              key={fieldRow.id}
              select={<InputLikeText left text={field?.label ?? '...'} />}
              input={
                <div data-searchid={fieldRow.name}>
                  <AdvancedSearchField
                    fields={fields}
                    index={index}
                    field={field}
                  />
                </div>
              }
              onRemove={() => {
                setRows(rows => rows.filter(row => row.id !== fieldRow.id));
                if (!field) return;
                methods.unregister(getSearchableFieldNames(field));

                augmented.submitForm.current?.();
              }}
              // TODO: Rimuovere?
              disableRemove={rows.length === 1}
            />
          );
        })}
        <SearchActions
          add={
            <AdvancedSearchFormItem
              name={`__filters_add`}
              label={t(
                'search.advanced-search.filter-add.label',
                'Aggiungi Filtro',
              )}
            >
              <AdvancedSearchSelectInput
                name={`__filters_add`}
                size="middle"
                placeholder={
                  <Space>
                    <PlusOutlined aria-hidden />
                    <HideOnMobileSpan>
                      {t(
                        'search.advanced-search.filter-add.select-placeholder',
                        'Aggiungi Filtro',
                      )}
                    </HideOnMobileSpan>
                  </Space>
                }
                searchable
                skipGroupSort
                onChange={(value, option, onChange) => {
                  setRows(rows => [...rows, { id: v4(), name: value }]);
                  onChange(null);

                  setTimeout(() => {
                    const element: HTMLInputElement | null =
                      document.querySelector(
                        `[data-searchid="${value}"] input:first-of-type`,
                      );
                    if (element != null && 'focus' in element) {
                      element.focus();
                    }
                  }, 100);
                }}
                skipResetOnMissingValue
                options={filterList}
                group
              />
            </AdvancedSearchFormItem>
          }
          extra={extraActions}
        />
      </Flex>
    </AdvancedSearchContainer>
  );
}
